import React, { useContext, FC, ReactElement, useState } from 'react';
import { useImpressionItem } from '@fiverr-private/impressionable';
import { tracker, EVENTS_MAPPING } from '@fiverr-private/listing_lib';
import { type FilterType, type VisualFilterOption, type tileSizeType, TILE_SIZES } from '@fiverr-private/listing_types';
import { TilesCarousel } from '../tiles_carousel/tilesCarousel';
import { FilterTile } from '../tiles/filter_tile/filterTile';
import { AppContext } from '../../context/listingsContext';
import { BI_EVENT_GROUPS } from '../../../../../shared/services/BIEvents/constants';
import { BI_EVENTS_TYPES, COMPONENT_NAMES, ELEMENT_TYPE } from '../../generic_listings/TagFilters/constants';
import { buildEventEnrichment } from '../../generic_listings/TagFilters/utils';
import { CHIP_MAX_TILES_TO_SHOW, CHIP_SLIDE_PADDING } from '../tiles_carousel/constants';
import { VIEW_ALL_COMPONENT } from './topFiltersCarousel.constants';
import { Title } from './title';
import type { TopFiltersCarouselProps } from './topFiltersCarousel.types';

import './index.scss';

export const CarouselContext = React.createContext<{
    filterId: string;
    subCategoryId?: string;
    allFilters: FilterType[];
    tileSize: tileSizeType;
    setStepIndex: (index: number) => void;
    stepIndex: number;
    hasNextStep: boolean;
    stepNum: number;
} | null>(null);

export const TopFiltersCarousel: FC<TopFiltersCarouselProps> = ({
    visualFilters = { stepIndex: 0, visualFiltersList: [] },
    subCategoryId,
}): ReactElement | null => {
    const { appFilters } = useContext(AppContext) || {};
    const { filters = [] } = appFilters || {};
    const [stepIndex, setStepIndex] = useState(visualFilters.stepIndex);
    const { visualFiltersList = [] } = visualFilters;
    const { id: filterId, options = [], tileSize = TILE_SIZES.MEDIUM } = visualFiltersList[stepIndex] || {};

    const enrichment = buildEventEnrichment({
        type: BI_EVENTS_TYPES.IMPRESSION,
        group: BI_EVENT_GROUPS.USER_IMPRESSION,
        elementName: filterId,
        elementType: ELEMENT_TYPE.VISUAL,
        componentName: COMPONENT_NAMES.VISUAL_FILTERS,
        elementMetadata: COMPONENT_NAMES.METADATA,
        elementPosition: 1,
    });

    const [ref] = useImpressionItem({ enrichment });

    if (!visualFiltersList.length) {
        return null;
    }

    const { options: filterOptions = [] } = filters.find((filter: VisualFilterOption) => filter.id === filterId) || {};

    const stepNum = stepIndex + 1;
    const showViewAllTile =
        filterOptions.length > options.length && !options.find(({ id }) => id === VIEW_ALL_COMPONENT);

    const shownOptions = showViewAllTile ? [...options, { id: VIEW_ALL_COMPONENT }] : options;

    const carouselContext = {
        filterId,
        subCategoryId,
        allFilters: filterOptions,
        tileSize,
        setStepIndex,
        stepIndex,
        hasNextStep: stepIndex < visualFiltersList.length - 1,
        stepNum,
    };

    return (
        <div ref={ref} className="top-filters-carousel">
            <CarouselContext.Provider value={carouselContext}>
                <Title />
                <div className="top-filters-carousel-wrapper">
                    <TilesCarousel
                        items={shownOptions}
                        TileComponent={FilterTile}
                        tilePadding={CHIP_SLIDE_PADDING}
                        maxTilesToShow={CHIP_MAX_TILES_TO_SHOW}
                        onArrowClick={() => tracker.trackTileFilter(EVENTS_MAPPING.FILTERS_ARROW, filterId, stepNum)}
                    />
                </div>
            </CarouselContext.Provider>
        </div>
    );
};
